import React, { ReactNode, useEffect, useMemo } from 'react'
import { Divider, Modal } from '@ui-kitten/components'
import { Platform, ScrollView, View, ViewStyle } from 'react-native'
import { useResponsive } from '../hooks'
import { BtSubTitle } from './BtText'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { colors } from '../theme'
import { composeStyle } from '../helpers'

type Props = {
  onClose: () => void
  visible: boolean
  width?: number
  onBackdropPress?: () => void
  closeOnBackDropPress?: boolean
  title?: string | ReactNode
  children: ReactNode
  footer?: ReactNode
  footerStyle?: ViewStyle
  headerStyle?: ViewStyle
}

export default ({
  onClose,
  visible,
  width = 400,
  closeOnBackDropPress = true,
  onBackdropPress,
  title,
  children,
  footer,
  footerStyle,
  headerStyle,
}: Props) => {
  const onKeyDown = (event) => {
    if (event.keyCode === 27 && visible) {
      onClose()
    }
  }

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.addEventListener('keydown', onKeyDown, false)
    }

    return () => {
      if (Platform.OS === 'web') {
        document.removeEventListener('keydown', onKeyDown, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { screenWidth, sm } = useResponsive()
  const HEADER_HEIGHT = sm ? 56 : 46
  // const CONTENT_HEIGHT = screenHeight - HEADER_HEIGHT - FOOTER_HEIGHT

  let scrollView
  React.useEffect(() => {
    if (!scrollView || !visible) return
    scrollView.scrollTo({ x: 0, y: 0, animated: false })
  }, [scrollView, visible])

  const header = useMemo(() => {
    return title ? (
      typeof title === 'string' ? (
        <View
          style={composeStyle(
            { height: HEADER_HEIGHT, width: '100%' },
            headerStyle,
          )}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              paddingHorizontal: 10,
            }}
          >
            <View style={{ flex: 1 }}>
              <BtSubTitle>{title}</BtSubTitle>
            </View>
            <MaterialCommunityIcons
              name="close-circle"
              size={20}
              onPress={onClose}
              color={colors.primary}
            />
          </View>
          <Divider />
        </View>
      ) : (
        title
      )
    ) : null

    return title
  }, [HEADER_HEIGHT, headerStyle, onClose, title])

  const _footer = useMemo(() => {
    if (!footer) return null

    return (
      <View
        style={composeStyle(
          {
            width: '100%',
          },
          footerStyle,
        )}
      >
        <Divider />
        <View style={{ flex: 1, padding: 10, paddingVertical: sm ? 20 : 10 }}>
          {footer}
        </View>
      </View>
    )
  }, [sm, footer, footerStyle])

  return (
    <Modal
      visible={visible}
      onBackdropPress={
        onBackdropPress
          ? onBackdropPress
          : closeOnBackDropPress
          ? onClose
          : undefined
      }
      backdropStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      style={composeStyle(
        {
          height: '100vh',
          backgroundColor: 'white',
          margin: 0,
          padding: 0,
        },
        sm ? { left: screenWidth - width, width } : { width: screenWidth },
      )}
    >
      {header}
      <ScrollView
        style={{
          padding: 10,
          flexGrow: 1,
          // height: CONTENT_HEIGHT,
        }}
        ref={(ref) => (scrollView = ref)}
      >
        {children}
      </ScrollView>
      {_footer}
    </Modal>
  )
}
